<template>
  <div class="locator-container" qaid="store-locator" :class="{'no-results': !hasStores}">
    <div class="p-2 grid-container fluid">
      <div>
        <section-header size="xl">
          <template v-slot:content>
            <heading :level="1" size="xl" qaid="store-locator__title" v-html="bp('storeFinderTitle')"></heading>
          </template>
          <template v-slot:actions>
            <trek-link
              v-if="viewBreakpointMoreThan('medium')"
              button
              text
              icon-right
              icon="arrow_forward"
              href="/store-finder/allRetailers/"
              qaid="store-locator__all-locations"
            >
              <span v-html="bp('allLocationsLinkText')"></span>
            </trek-link>
          </template>
        </section-header>
      </div>
      <div v-if="isTrekLocator">
        <template v-if="viewBreakpointMoreThan('medium')">
          <!-- Desktop -->
          <div class="grid-x grid-margin-x locator-search-wrapper">
            <div class="cell large-6 xlarge-5 xxlarge-4 flex flex-col relative locator-sidebar">
              <locator-search
                v-show="!quickViewId"
                ref="locatorSearch"
                v-model="textSearchQuery"
                :base-properties="baseProperties"
                :is-loading="isLoading"
                :geoip-location="geoipLocation"
                qaid="store-locator__search"
                @search="newSearch"
                @open-filters="openFilters"
                @filter-removed="removeFilter"
                @query-center="updateMapCoords"
              />
              <locator-current-filters-list
                v-show="!quickViewId"
                :filters="currentFiltersList"
                qaid="store-locator__current-filters"
                @filter-removed="removeFilter"
              />
              <locator-store-list
                v-show="!quickViewId"
                v-model="stores"
                :base-properties="baseProperties"
                :display-empty-message="!isLoading"
                :is-loading="isLoading"
                :pin-hover-id="pinHoverId"
                qaid="store-locator__store-list"
                @store-clicked="openQuickView"
                @card-hover="cardHover"
              />
              <pagination
                v-show="!quickViewId"
                v-if="totalPages > 1 && stores.length"
                class="max-w-xs w-full m-auto"
                container-class="justify-between"
                :display-pages="false"
                :page-number="pagination.page"
                :page-count="totalPages"
                @page-change="pageChange"
                @prev-page="prevPage"
                @next-page="nextPage"
              >
                <template v-slot:previous>
                  <span class="text-md ml-1">{{ bp('paginationPrevious') }}</span>
                </template>
                <template v-slot:next>
                  <span class="text-md mr-1">{{ bp('paginationNext') }}</span>
                </template>
              </pagination>
              <locator-filters
                v-if="isFiltersPanelVisible"
                :base-properties="baseProperties"
                :filters="filters"
                :service-filters="allowedServiceFilters"
                :sale-filters="saleFilters"
                qaid="store-locator__filters"
                @apply-filters="applyFilters"
                @close-filters="closeFilters"
              />
              <locator-quick-view
                v-if="isQuickViewVisible"
                :base-properties="baseProperties"
                :value="quickViewId"
                :store="quickViewStoreData"
                qaid="store-locator__quick-view"
                @close-quick-view="closeQuickView"
                @go-to-store-page="goToStorePage"
                @reload-data="loadData"
              />
            </div>
            <div class="cell large-6 xlarge-7 xxlarge-8">
              <locator-map
                :base-properties="baseProperties"
                :card-hover-id="cardHoverId"
                :quick-view-id="quickViewId"
                :stores="stores"
                :center-coords="centerCoords"
                :zoom-level="zoom"
                :bounds-to-set="boundsToSet"
                :bounds-to-set-by-center-and-zoom="boundsToSetByCenterAndZoom"
                :show-debug="false"
                :is-loading="isLoading"
                map-classes="rl-map"
                class="relative"
                qaid="store-locator__map"
                @new-coords="updateMapCoords"
                @pin-hover="pinHover"
                @user-changed-map="doBoundsBasedQuery"
                @store-clicked="openQuickView"
              />
            </div>
          </div>
        </template>

        <template v-else>
          <!-- Mobile presentation -->
          <locator-search
            v-show="!isQuickViewVisible"
            ref="locatorSearch"
            v-model="textSearchQuery"
            :base-properties="baseProperties"
            :is-loading="isLoading"
            :geoip-location="geoipLocation"
            qaid="store-locator__search--mobile"
            @search="newSearch"
            @open-filters="openFilters"
            @filter-removed="removeFilter"
            @query-center="updateMapCoords"
          />
          <locator-current-filters-list
            v-show="!isQuickViewVisible"
            :filters="currentFiltersList"
            class="overflow-auto whitespace-nowrap flex-nowrap"
            qaid="store-locator__current-filters--mobile"
            @filter-removed="removeFilter"
          />
          <tabs v-show="!quickViewId" v-model="currentMode" qaid="store-locator__tabs">
            <tab-pane name="list" :label="bp('storeFinderList')" class="store-locator__tab-pane flex flex-col">
              <locator-store-list
                v-show="!quickViewId"
                v-model="stores"
                :base-properties="baseProperties"
                :display-empty-message="!isLoading"
                :is-loading="isLoading"
                :pin-hover-id="pinHoverId"
                qaid="store-locator__store-list--mobile"
                @store-clicked="openQuickView"
                @card-hover="cardHover"
              />
              <pagination
                v-if="totalPages > 1 && stores.length"
                container-class="justify-between"
                :display-pages="false"
                :page-number="pagination.page"
                :page-count="totalPages"
                @page-change="pageChange"
                @prev-page="prevPage"
                @next-page="nextPage"
              >
                <template v-slot:previous>
                  <span class="text-md ml-1">{{ bp('paginationPrevious') }}</span>
                </template>
                <template v-slot:next>
                  <span class="text-md mr-1">{{ bp('paginationNext') }}</span>
                </template>
              </pagination>
            </tab-pane>
            <tab-pane name="map" :label="bp('storeFinderMap')" class="store-locator__tab-pane">
              <locator-map
                :base-properties="baseProperties"
                :card-hover-id="cardHoverId"
                :stores="stores"
                :center-coords="centerCoords"
                :zoom-level="zoom"
                :bounds-to-set="currentMode === 'map' ? boundsToSet : null"
                :bounds-to-set-by-center-and-zoom="currentMode === 'map' ? boundsToSetByCenterAndZoom : null"
                :show-debug="false"
                :is-loading="isLoading"
                map-classes="rl-map"
                qaid="store-locator__map--mobile"
                class="h-full"
                @new-coords="updateMapCoords"
                @pin-hover="pinHover"
                @user-changed-map="doBoundsBasedQuery"
                @store-clicked="openQuickView"
              />
            </tab-pane>
          </tabs>
          <locator-filters
            v-if="isFiltersPanelVisible"
            :base-properties="baseProperties"
            :filters="filters"
            :service-filters="allowedServiceFilters"
            :sale-filters="saleFilters"
            qaid="store-locator__filters--mobile"
            @apply-filters="applyFilters"
            @close-filters="closeFilters"
          />
          <locator-quick-view
            v-if="isQuickViewVisible"
            :base-properties="baseProperties"
            :value="quickViewId"
            :store="quickViewStoreData"
            qaid="store-locator__quick-view--mobile"
            @close-quick-view="closeQuickView"
            @go-to-store-page="goToStorePage"
            @reload-data="loadData"
          />
        </template>
      </div>
      <locally-locator v-else :base-properties="baseProperties" :options="options.locally" />
      <div v-if="viewBreakpointLessThan('large')" class="text-center mt-2">
        <trek-link
          button
          text
          icon-right
          icon="arrow_forward"
          href="/store-finder/allRetailers/"
          qaid="store-locator__all-locations"
        >
          <span v-html="bp('allLocationsLinkText')"></span>
        </trek-link>
      </div>
      <div class="max-w-md mx-auto my-4 md:my-6 text-sm text-gray-60" qaid="store-locator__description">
        <p class="mb-2 font-bold" v-html="bp('storeFinderWhoTitle')"></p>
        <p class="mb-2" v-html="bp('storeFinderWhoBody')"></p>
        <p class="mb-2 font-bold" v-html="bp('storeFinderWhyTitle')"></p>
        <p class="mb-2" v-html="bp('storeFinderWhyBody1')"></p>
        <p class="mb-2" v-html="bp('storeFinderWhyBody2')"></p>
        <p class="mb-2" v-html="bp('storeFinderWhyBody3')"></p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import {Url} from '@/utils/url';
import viewSize from '@/mixins/view-size';
import LocatorSearch from '@/components/locator/components/LocatorSearch';
import LocatorStoreList from '@/components/locator/components/LocatorStoreList';
import {QuickView} from '@/components/locator/mixins/quick-view';
import {Filters} from '@/components/locator/mixins/filters';
import {LocatorApi} from '@/components/locator/mixins/locator-api';
import LocatorCurrentFiltersList from '@/components/locator/components/LocatorCurrentFiltersList';
import SectionHeader from '@/components/SectionHeader';
import LocatorMap from '@/components/locator/components/LocatorMap';
import Heading from '@/components/Heading';
import Pagination from '@/components/Pagination';
import Tabs from '@/components/tabs/Tabs';
import TabPane from '@/components/tabs/TabPane';
import LocallyLocator from '@/components/locator/components/LocallyLocator';
import TrekLink from '@/components/TrekLink';

export default {
  name: 'LocatorContainer',

  components: {
    LocatorCurrentFiltersList,
    LocatorStoreList,
    LocatorSearch,
    SectionHeader,
    LocatorMap,
    Heading,
    Pagination,
    Tabs,
    TabPane,
    LocallyLocator,
    TrekLink,
  },

  mixins: [viewSize, QuickView, Filters, LocatorApi],

  props: {
    baseProperties: {
      type: Array,
      default: () => [],
    },
    geoipLocation: {
      type: String,
      default: undefined,
    },
    geoipLat: {
      type: Number,
      default: 43.1790849, // Trek HQ, Waterloo, WI
    },
    geoipLng: {
      type: Number,
      default: -89.0058727,
    },
    isTrekLocator: {
      type: Boolean,
      default: true,
    },
    options: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      currentMode: 'list',
      quickViewStoreData: Object,
      textSearchQuery: '',
      stores: [],
      cardHoverId: -1,
      pinHoverId: -1,
      quickViewId: null,
      centerCoords: {},
      initialZoom: 11,
      zoom: 11,
      initialCoordinates: {lat: this.geoipLat, lng: this.geoipLng},
      boundsToSet: null,
      boundsToSetByCenterAndZoom: null,
      boundsToSetByTextQuery: null,
      pagination: {},
      isRestoringState: false,
      contextPath: ACC.config.encodedContextPath || '',
    };
  },

  computed: {
    totalPages() {
      if (!this.pagination.pageSize) {
        return 1;
      }
      return Math.ceil(this.pagination.totalResults / this.pagination.pageSize);
    },
  },

  watch: {
    textSearchQuery() {
      if (!this.isRestoringState) {
        this.newSearch();
      }
    },
  },

  created() {
    this.centerCoords = this.initialCoordinates;
    if (this.filterParams) {
      this.$set(this.filters, 'services', this.filterParams.join(','));

      // Remove filters param from URL so that vuex state can be restored upon returning to the page
      window.history.replaceState(
        null,
        document.title,
        Url.update(window.location.href, undefined, {
          removeKeys: ['filters'],
        })
      );
    }

    // Restore state from vuex
    else if (
      this.$store.getters['storeLocator/textSearchQuery'] &&
      !isEmpty(this.$store.getters['storeLocator/mapCenterCoords'])
    ) {
      this.isRestoringState = true;
      this.restoreState();
    } else {
      this.$store.dispatch('storeLocator/resetCache');
    }
  },

  mounted() {
    if (!this.isRestoringState && this.isTrekLocator) {
      this.$refs.locatorSearch.scheduleGeolocationQuery();
    } else {
      this.$nextTick(() => {
        this.isRestoringState = false;
      });
    }
  },

  methods: {
    doBoundsBasedQuery(bounds) {
      this.resetBoundsToSet();
      this.searchBounds = bounds;
      this.loadData();
    },

    updateMapCoords(center, zoom) {
      this.resetBoundsToSet();
      this.centerCoords = {...center};
      if (zoom) {
        this.zoom = zoom;
      }
    },

    resetBoundsToSet() {
      this.boundsToSet = null;
      this.boundsToSetByCenterAndZoom = null;
      this.boundsToSetByTextQuery = null;
    },

    closeQuickView() {
      this.quickViewId = null;
    },

    openQuickView(storeId, distance) {
      let timeStamp = new Date().getTime();
      let url = `/store-finder/store?timestamp=${timeStamp}&id=${storeId}&distance=${distance}`;
      axios.get(url).then(({data}) => {
        this.quickViewStoreData = data.data;
        this.quickViewId = storeId;
      });
    },

    goToStorePage(storeId) {
      // Save state to vuex before navigating away
      this.saveState();
      this.$nextTick(() => {
        window.location.href = `${this.contextPath}/store/${storeId}/`;
      });
    },

    cardHover(index) {
      this.cardHoverId = index;
    },

    pinHover(index) {
      this.pinHoverId = index;
    },

    pageChange(page) {
      this.loadData(page);
    },

    prevPage() {
      if (this.pagination.page > 1) {
        this.pageChange(this.pagination.page - 1);
      }
    },

    nextPage() {
      if (this.pagination.page < this.totalPages) {
        this.pageChange(this.pagination.page + 1);
      }
    },

    setMapBounds(payload) {
      if (
        !isEmpty(payload.geoPointForSearchTerm) &&
        (payload.isNewTextSearch || (payload.previousPage > 0 && payload.currentPage === 0) || payload.isFilterChange)
      ) {
        /**
         * When user is doing a new text-based query,
         * or is returning to first page,
         * or has changed their filters,
         * we want to set a happy medium zoom level for displaying results
         * and then zoom out if necessary to ensure at least one store is shown on the map
         */
        let storeToIncludeOnMap = this.stores[0].id;
        if (this.stores.length > 1 && this.stores[0].isMyStore) {
          storeToIncludeOnMap = this.stores[1].id;
        }
        this.boundsToSet = null;
        this.boundsToSetByCenterAndZoom = {
          centerCoords: {
            lat: payload.geoPointForSearchTerm.latitude,
            lng: payload.geoPointForSearchTerm.longitude,
          },
          zoom: this.initialZoom,
          extendBoundsForStore: storeToIncludeOnMap,
        };
      } else if (payload.previousPage != payload.currentPage && !isEmpty(payload.displayBounds)) {
        /**
         * Otherwise, when user is changing pages we'll use the boundaries provided by backend that encompass
         * all the stores in the result set.
         */
        this.boundsToSetByCenterAndZoom = null;
        this.boundsToSet = payload.displayBounds;
      } else if (isEmpty(payload.geoPointForSearchTerm) && isEmpty(payload.displayBounds)) {
        // We can use a google geocoder request as fallback
        this.zoom = this.initialZoom;
        this.boundsToSetByTextQuery = this.textSearchQuery;
      }
    },

    saveState() {
      /**
       * Overcome back-forward caching on safari
       * Subject to webkit bug https://bugs.webkit.org/show_bug.cgi?id=156356
       * (Returning to this page with back button more than once fails to fire the 'pageshow' event)
       */
      document.getElementsByTagName('BODY')[0].onpageshow = (e) => {
        if (e.persisted) {
          this.restoreState();
        }
      };
      this.$store.commit('storeLocator/setTextSearchQuery', this.textSearchQuery);
      this.$store.commit('storeLocator/setActiveFilters', this.filters);
      this.$store.commit('storeLocator/setPagination', this.pagination);
      this.$store.commit('storeLocator/setMapCenterCoords', this.centerCoords);
      this.$store.commit('storeLocator/setMapZoom', this.zoom);
      this.$store.commit('storeLocator/setSearchBounds', this.searchBounds);
    },

    restoreState() {
      this.isLoading = true;
      this.quickViewId = null;
      this.textSearchQuery = this.$store.getters['storeLocator/textSearchQuery'];
      this.filters = this.$store.getters['storeLocator/activeFilters'];
      this.pagination = this.$store.getters['storeLocator/pagination'];
      this.initialCoordinates = this.$store.getters['storeLocator/mapCenterCoords'];
      this.centerCoords = this.$store.getters['storeLocator/mapCenterCoords'];
      this.searchBounds = this.$store.getters['storeLocator/searchBounds'];
      this.zoom = this.$store.getters['storeLocator/mapZoom'];
      this.$store.dispatch('storeLocator/resetCache');

      this.loadData(this.pagination.page);
    },
  },
};
</script>
